import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import siteConfig from "../config/siteConfig";

export const CartContext = createContext({
  cartItems: [],
  wishlistItems: [],
  addToCart: () => {},
  removeFromCart: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  addToWishlist: () => {},
  removeFromWishlist: () => {},
  toggleWishlist: () => {},
  clearWishlist: () => {},
  isWishlisted: () => false,
});

const readStoredArray = (key) => {
  if (typeof window === "undefined") return [];

  try {
    const storedValue = window.localStorage.getItem(key);
    const parsedValue = storedValue ? JSON.parse(storedValue) : [];
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch {
    return [];
  }
};

const persistArray = (key, value) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // The storefront remains usable when storage is unavailable or full.
  }
};

const getProductId = (productOrId) =>
  typeof productOrId === "object" && productOrId !== null
    ? productOrId.id
    : productOrId;

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStoredArray(siteConfig.storageKeys.cart)
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    readStoredArray(siteConfig.storageKeys.wishlist)
  );

  useEffect(() => {
    persistArray(siteConfig.storageKeys.cart, cartItems);
  }, [cartItems]);

  useEffect(() => {
    persistArray(siteConfig.storageKeys.wishlist, wishlistItems);
  }, [wishlistItems]);

  const addToCart = useCallback((productId, quantity = 1, size, color) => {
    const quantityToAdd = Math.max(1, Math.floor(Number(quantity) || 1));

    setCartItems((previousItems) => {
      const existingItem = previousItems.find(
        (item) =>
          item.id === productId && item.size === size && item.color === color
      );

      if (!existingItem) {
        return [
          ...previousItems,
          { id: productId, size, color, quantity: quantityToAdd },
        ];
      }

      return previousItems.map((item) =>
        item.id === productId && item.size === size && item.color === color
          ? { ...item, quantity: item.quantity + quantityToAdd }
          : item
      );
    });
  }, []);

  const removeFromCart = useCallback((productId, size, color) => {
    setCartItems((previousItems) =>
      previousItems.filter(
        (item) =>
          !(item.id === productId && item.size === size && item.color === color)
      )
    );
  }, []);

  const updateQuantity = useCallback((productId, size, color, quantity) => {
    const nextQuantity = Math.max(1, Math.floor(Number(quantity) || 1));

    setCartItems((previousItems) =>
      previousItems.map((item) =>
        item.id === productId && item.size === size && item.color === color
          ? { ...item, quantity: nextQuantity }
          : item
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const addToWishlist = useCallback((productOrId) => {
    const productId = getProductId(productOrId);
    if (productId === undefined || productId === null) return;

    setWishlistItems((previousItems) =>
      previousItems.includes(productId)
        ? previousItems
        : [...previousItems, productId]
    );
  }, []);

  const removeFromWishlist = useCallback((productOrId) => {
    const productId = getProductId(productOrId);
    setWishlistItems((previousItems) =>
      previousItems.filter((itemId) => itemId !== productId)
    );
  }, []);

  const toggleWishlist = useCallback((productOrId) => {
    const productId = getProductId(productOrId);
    if (productId === undefined || productId === null) return;

    setWishlistItems((previousItems) =>
      previousItems.includes(productId)
        ? previousItems.filter((itemId) => itemId !== productId)
        : [...previousItems, productId]
    );
  }, []);

  const clearWishlist = useCallback(() => {
    setWishlistItems([]);
  }, []);

  const isWishlisted = useCallback(
    (productOrId) => wishlistItems.includes(getProductId(productOrId)),
    [wishlistItems]
  );

  const contextValue = useMemo(
    () => ({
      cartItems,
      wishlistItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      clearWishlist,
      isWishlisted,
    }),
    [
      addToCart,
      addToWishlist,
      cartItems,
      clearCart,
      clearWishlist,
      isWishlisted,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      wishlistItems,
    ]
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};
